import { create } from "zustand";
import {
  getSimulationState,
  sendMessage,
  endSimulation,
  getInterimSummary,
  switchPersona,
  API,
} from "./api";

export const useSimulationStore = create((set, get) => ({
  // --- State ---
  sessionId: null,
  dealState: null,
  messages: [],
  coachInterventions: [],
  loading: true,
  sending: false,
  simView: "chat", // chat | interim | completed
  voiceMode: false,
  interimSummary: null,
  interimLoading: false,
  endReason: null,
  showEndDialog: false,
  latestCoachFeedback: null,
  _coachSource: null,

  // --- Init ---
  initSession: async (sessionId) => {
    set({ sessionId, loading: true });
    try {
      const res = await getSimulationState(sessionId);
      set({
        dealState: res.data.deal_state,
        messages: res.data.messages,
        coachInterventions: res.data.coach_interventions,
        loading: false,
      });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  // --- Send Message (streaming with non-streaming fallback) ---
  handleSendMessage: async (message) => {
    const { sessionId, dealState, sending } = get();
    if (!message.trim() || sending) return {};

    set({ sending: true });
    const userMsg = {
      role: "user",
      content: message,
      turn_number: (dealState?.session_clock || 0) + 1,
    };
    set((s) => ({ messages: [...s.messages, userMsg] }));

    try {
      return await get()._streamTurn(message);
    } catch (err) {
      if (err && err.streamNotStarted) {
        try {
          const res = await sendMessage(sessionId, message);
          const d = res.data;
          if (d.persona_response) {
            set((s) => ({ messages: [...s.messages, d.persona_response] }));
          }
          return get()._applyTurnResult(d);
        } catch (err2) {
          set((s) => ({ messages: s.messages.slice(0, -1) }));
          throw err2;
        }
      }
      throw err;
    } finally {
      set({ sending: false });
    }
  },

  // Shared post-turn state handling (deal state, coach, end, plateau)
  _applyTurnResult: (d) => {
    set({ dealState: d.deal_state });
    if (d.coach_feedback) {
      set((s) => ({
        coachInterventions: [...s.coachInterventions, d.coach_feedback],
        latestCoachFeedback: d.coach_feedback,
      }));
    } else if (d.coach_pending && !d.simulation_ended) {
      get()._subscribeToCoach();
    }
    if (d.simulation_ended) {
      set({ endReason: d.end_reason, simView: "completed" });
    }
    if (!d.simulation_ended && d.plateau_detected) {
      get().handleShowInterim();
    }
    return {
      phase_transitioned: d.phase_transitioned,
      new_phase: d.new_phase,
      simulation_ended: d.simulation_ended,
    };
  },

  // SSE-over-fetch: streams persona tokens with a smooth typewriter reveal
  _streamTurn: async (message) => {
    const { sessionId } = get();
    const streamId = `stream-${Date.now()}`;
    let started = false;
    let doneReceived = false;
    let target = "";
    let revealed = 0;
    let ticker = null;

    const updateMsg = (patch) =>
      set((s) => ({
        messages: s.messages.map((m) => (m.id === streamId ? { ...m, ...patch } : m)),
      }));

    const stopTicker = () => {
      if (ticker) {
        clearInterval(ticker);
        ticker = null;
      }
    };
    const startTicker = () => {
      if (ticker) return;
      ticker = setInterval(() => {
        if (revealed < target.length) {
          revealed = Math.min(target.length, revealed + (doneReceived ? 30 : 6));
          updateMsg({ content: target.slice(0, revealed) });
        } else if (doneReceived) {
          stopTicker();
          updateMsg({ streaming: false, content: target });
        }
      }, 24);
    };

    let resp;
    try {
      resp = await fetch(`${API}/simulation/chat-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message }),
      });
    } catch (e) {
      e.streamNotStarted = true;
      throw e;
    }
    if (!resp.ok || !resp.body) {
      const e = new Error(`Stream failed (${resp.status})`);
      e.streamNotStarted = true;
      throw e;
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let result = {};

    const handleEvent = (ev) => {
      if (ev.type === "meta") {
        started = true;
        set((s) => ({
          messages: [
            ...s.messages,
            {
              id: streamId,
              role: "persona",
              content: "",
              persona_id: ev.persona_id,
              persona_name: ev.persona_name,
              trust_delta: ev.trust_delta,
              streaming: true,
            },
          ],
        }));
      } else if (ev.type === "delta") {
        target += ev.t;
        startTicker();
      } else if (ev.type === "replace") {
        target = ev.content;
        revealed = 0;
        startTicker();
      } else if (ev.type === "done") {
        doneReceived = true;
        target = ev.persona_response?.content ?? target;
        if (revealed > target.length) revealed = target.length;
        startTicker();
        result = get()._applyTurnResult(ev);
      } else if (ev.type === "error") {
        const e = new Error(ev.detail || "Stream error");
        if (!started) e.streamNotStarted = true;
        throw e;
      }
    };

    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = raw.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          handleEvent(JSON.parse(line.slice(6)));
        }
      }
    } catch (e) {
      stopTicker();
      if (started) {
        updateMsg({ streaming: false, content: target });
      }
      throw e;
    }

    if (!doneReceived) {
      stopTicker();
      if (started) updateMsg({ streaming: false, content: target });
      const e = new Error("Stream ended unexpectedly");
      if (!started) e.streamNotStarted = true;
      throw e;
    }
    return result;
  },

  // --- SSE Coach Subscription ---
  _subscribeToCoach: () => {
    const { sessionId, _coachSource } = get();
    if (_coachSource) _coachSource.close();

    const source = new EventSource(`${API}/simulation/${sessionId}/coach-stream`);
    set({ _coachSource: source });

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.ready) {
          if (data.coach_feedback) {
            set((s) => ({
              coachInterventions: [...s.coachInterventions, data.coach_feedback],
              latestCoachFeedback: data.coach_feedback,
            }));
          }
          if (data.deal_state) {
            set({ dealState: data.deal_state });
          }
          if (data.plateau_detected) {
            get().handleShowInterim();
          }
          source.close();
          set({ _coachSource: null });
        }
      } catch {
        // parse error, ignore
      }
    };

    source.onerror = () => {
      source.close();
      set({ _coachSource: null });
    };
  },

  // --- Interim Summary ---
  handleShowInterim: async () => {
    const { sessionId } = get();
    set({ interimLoading: true, simView: "interim" });
    try {
      const res = await getInterimSummary(sessionId);
      set({
        interimSummary: res.data.summary,
        dealState: res.data.deal_state,
        interimLoading: false,
      });
    } catch {
      set({ simView: "chat", interimLoading: false });
    }
  },

  // --- Switch Persona ---
  handleSwitchPersona: async (targetPersona) => {
    const { sessionId } = get();
    const res = await switchPersona(sessionId, targetPersona);
    const { greeting, deal_state, new_persona_name } = res.data;
    set((s) => ({
      dealState: deal_state,
      messages: [
        ...s.messages,
        { role: "system", content: `Conversation transitioned to ${new_persona_name}` },
        greeting,
      ],
      interimSummary: null,
      simView: "chat",
      voiceMode: false,
    }));
    return new_persona_name;
  },

  // --- End Simulation ---
  handleEndSimulation: async () => {
    const { sessionId } = get();
    await endSimulation(sessionId);
    set({ endReason: "user_ended", simView: "completed", showEndDialog: false });
  },

  // --- Voice Response Handler ---
  handleVoiceResponse: (data) => {
    const {
      persona_response,
      coach_feedback,
      deal_state,
      simulation_ended,
      end_reason,
      transcribed_text,
      plateau_detected,
    } = data;

    set({ dealState: deal_state });

    if (transcribed_text) {
      set((s) => ({
        messages: [
          ...s.messages,
          { role: "user", content: transcribed_text, turn_number: deal_state?.session_clock || 0 },
        ],
      }));
    }
    if (persona_response) {
      set((s) => ({ messages: [...s.messages, persona_response] }));
    }
    if (coach_feedback) {
      set((s) => ({
        coachInterventions: [...s.coachInterventions, coach_feedback],
        latestCoachFeedback: coach_feedback,
      }));
    }
    if (simulation_ended) {
      set({ endReason: end_reason, simView: "completed" });
    }
    if (!simulation_ended && plateau_detected) {
      get().handleShowInterim();
    }
  },

  // --- UI Actions ---
  setVoiceMode: (v) => set({ voiceMode: v }),
  setSimView: (v) => set({ simView: v }),
  setShowEndDialog: (v) => set({ showEndDialog: v }),
  continueCurrent: () => set({ interimSummary: null, simView: "chat" }),

  cleanup: () => {
    const { _coachSource } = get();
    if (_coachSource) _coachSource.close();
  },
}));
